import { getAuthToken, getImpersonationUserId } from '@/lib/auth-cache';

/**
 * Thin `fetch` wrapper that attaches the cached Supabase auth token as an
 * `Authorization: Bearer <token>` header.
 *
 * All other `init` options (method, body, signal, credentials, etc.) and any
 * caller-provided headers are preserved; the Authorization header is added on
 * top (so callers never need to plumb the token themselves). When no token is
 * available the request is sent without an Authorization header.
 *
 * Attaches `X-Impersonate-User` from sessionStorage when impersonation is
 * active (single source of truth, mirrored by the global interceptor in
 * `impersonation-context.tsx` for non-helper `fetch()` paths). A caller-supplied
 * `X-Impersonate-User` in `init.headers` still wins.
 *
 * @example
 *   const res = await authedFetch(`/api/organizations/${id}/metrics`, {
 *     method: 'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify(payload),
 *   });
 *   if (!res.ok) throw new Error('Failed');
 */
export async function authedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const token = await getAuthToken();
  const impersonateId = getImpersonationUserId();
  // Normalize via `new Headers(...)` so ALL valid HeadersInit shapes (Headers instance,
  // entries array, or plain object) are preserved — a plain-object spread silently drops
  // Headers instances and mangles entries arrays. Mirrors impersonation-context.tsx.
  const headers = new Headers(init.headers);
  if (impersonateId && !headers.has('X-Impersonate-User')) {
    headers.set('X-Impersonate-User', impersonateId);
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(input, { ...init, headers });
}
